/**
 * MyProfilePopup for Poker Reborn
 *
 * Displays the hero's own profile when clicking avatar/name in RoomBar/LobbyBar.
 * Uses accountService.getLastState() for profile data (already fetched on login).
 * Supports inline nickname editing via DOM input overlay.
 *
 * @see docs/screens/room/popups/MY_PROFILE_POPUP.md - Visual specs, behavior
 */

import { Assets, Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { Button, GOLD_BUTTON_STYLE, PRIMARY_BUTTON_STYLE } from '@/components/common/Button';
import { accountService, type AccountErrorInfo } from '@/services/AccountService';
import { networkService } from '@/services/NetworkService';
import { avatarService } from '@/services/AvatarService';
import { getAvatarPath } from '@/config/avatars';
import { formatBalance } from '@/utils/format';
import { ServiceGLIDs, Commands } from '@/types/protocol';
import { decodeProfile } from '@/utils/profile';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  overlay: {
    color: 0x000000,
    alpha: 0.7,
  },

  popup: {
    width: 320,
    height: 380,
    fill: 0x1a1a2e,
    stroke: 0x3a3a5e,
    cornerRadius: 15,
  },

  avatar: {
    size: 64,
    borderColor: 0xd4af37,
    borderWidth: 3,
    backgroundColor: 0x333333,
  },

  title: {
    fill: 0xffffff,
    fontSize: 14,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'normal' as const,
  },

  displayName: {
    fill: 0xffffff,
    fontSize: 22,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },

  nicknameHint: {
    fill: 0x888888,
    fontSize: 12,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontStyle: 'italic' as const,
  },

  chipsLabel: {
    fill: 0x888888,
    fontSize: 14,
    fontFamily: 'Roboto, Arial, sans-serif',
  },

  chipsValue: {
    fill: 0xffd700,
    fontSize: 28,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },

  loading: {
    fill: 0x666666,
    fontSize: 16,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontStyle: 'italic' as const,
  },

  error: {
    fill: 0xff4444,
    fontSize: 13,
    fontFamily: 'Roboto, Arial, sans-serif',
  },
};

/** Max nickname length (server enforces 1-20) */
const NICKNAME_MAX_LENGTH = 20;

// =============================================================================
// MY PROFILE POPUP
// =============================================================================

export class MyProfilePopup extends Container {
  private overlay: Graphics;
  private popupContainer: Container;
  private background: Graphics;
  private titleText: Text;
  private displayNameText: Text;
  private nicknameHintText: Text;
  private chipsLabelText: Text;
  private chipsValueText: Text;
  private loadingText: Text;
  private nicknameErrorText: Text;
  private closeButton: Button;
  private changeAvatarButton: Button;
  private editNicknameButton: Button;
  private avatarContainer: Container;

  // Edit mode elements
  private nicknameInput: HTMLInputElement | null = null;
  private saveNicknameButton: Button | null = null;
  private cancelNicknameButton: Button | null = null;
  private isEditing = false;

  private screenWidth: number;
  private screenHeight: number;

  /** Callback when popup is dismissed */
  onDismiss: (() => void) | null = null;

  /** Callback to open avatar selection */
  onChangeAvatar: (() => void) | null = null;

  constructor(screenWidth: number, screenHeight: number) {
    super();

    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    // Create overlay
    this.overlay = new Graphics();
    this.overlay.rect(0, 0, screenWidth, screenHeight);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });
    this.overlay.eventMode = 'static';
    this.overlay.cursor = 'pointer';
    this.overlay.on('pointerdown', () => { this.handleDismiss(); });
    this.addChild(this.overlay);

    // Create popup container (centered)
    this.popupContainer = new Container();
    this.popupContainer.x = screenWidth / 2;
    this.popupContainer.y = screenHeight / 2;
    this.addChild(this.popupContainer);

    // Create background
    this.background = new Graphics();
    this.popupContainer.addChild(this.background);
    this.drawBackground();

    // Block popup clicks from reaching overlay
    this.background.eventMode = 'static';
    this.background.on('pointerdown', (e) => { e.stopPropagation(); });

    const halfHeight = STYLES.popup.height / 2;

    // Title
    this.titleText = new Text({ text: 'My Profile', style: STYLES.title });
    this.titleText.anchor.set(0.5);
    this.titleText.y = -halfHeight + 30;
    this.popupContainer.addChild(this.titleText);

    // Avatar display
    this.avatarContainer = new Container();
    this.avatarContainer.y = -halfHeight + 80;
    this.popupContainer.addChild(this.avatarContainer);
    this.drawAvatarDisplay();

    // Display name (nickname if set, otherwise username)
    this.displayNameText = new Text({ text: '', style: STYLES.displayName });
    this.displayNameText.anchor.set(0.5);
    this.displayNameText.y = -40;
    this.displayNameText.visible = false;
    this.popupContainer.addChild(this.displayNameText);

    // Nickname hint text (shows "@username" when nickname is set, or "No nickname set")
    this.nicknameHintText = new Text({ text: '', style: STYLES.nicknameHint });
    this.nicknameHintText.anchor.set(0.5);
    this.nicknameHintText.y = -18;
    this.nicknameHintText.visible = false;
    this.popupContainer.addChild(this.nicknameHintText);

    // Nickname error text (shown during edit mode)
    this.nicknameErrorText = new Text({ text: '', style: STYLES.error });
    this.nicknameErrorText.anchor.set(0.5);
    this.nicknameErrorText.y = -18;
    this.nicknameErrorText.visible = false;
    this.popupContainer.addChild(this.nicknameErrorText);

    // Chips label
    this.chipsLabelText = new Text({ text: 'Balance', style: STYLES.chipsLabel });
    this.chipsLabelText.anchor.set(0.5);
    this.chipsLabelText.y = 10;
    this.chipsLabelText.visible = false;
    this.popupContainer.addChild(this.chipsLabelText);

    // Chips value
    this.chipsValueText = new Text({ text: '', style: STYLES.chipsValue });
    this.chipsValueText.anchor.set(0.5);
    this.chipsValueText.y = 38;
    this.chipsValueText.visible = false;
    this.popupContainer.addChild(this.chipsValueText);

    // Loading text
    this.loadingText = new Text({ text: 'Loading...', style: STYLES.loading });
    this.loadingText.anchor.set(0.5);
    this.loadingText.y = 20;
    this.popupContainer.addChild(this.loadingText);

    // Change Avatar button
    const actionBtnWidth = 160;
    const actionBtnHeight = 34;
    this.changeAvatarButton = new Button({
      label: 'Change Avatar',
      width: actionBtnWidth,
      height: actionBtnHeight,
      style: { ...GOLD_BUTTON_STYLE, fontSize: 13 },
    });
    this.changeAvatarButton.onClick = () => { this.onChangeAvatar?.(); };
    this.changeAvatarButton.x = -actionBtnWidth / 2;
    this.changeAvatarButton.y = halfHeight - 130;
    this.popupContainer.addChild(this.changeAvatarButton);

    // Edit Nickname button
    this.editNicknameButton = new Button({
      label: 'Edit Nickname',
      width: actionBtnWidth,
      height: actionBtnHeight,
      style: { ...GOLD_BUTTON_STYLE, fontSize: 13 },
    });
    this.editNicknameButton.onClick = () => { this.enterEditMode(); };
    this.editNicknameButton.x = -actionBtnWidth / 2;
    this.editNicknameButton.y = halfHeight - 90;
    this.popupContainer.addChild(this.editNicknameButton);

    // Close button
    const closeBtnWidth = 100;
    const closeBtnHeight = 36;
    this.closeButton = new Button({
      label: 'Close',
      width: closeBtnWidth,
      height: closeBtnHeight,
      style: PRIMARY_BUTTON_STYLE,
    });
    this.closeButton.onClick = () => { this.handleDismiss(); };
    this.closeButton.x = -closeBtnWidth / 2;
    this.closeButton.y = halfHeight - 48;
    this.popupContainer.addChild(this.closeButton);

    // Show profile data
    this.fetchProfile();
  }

  /** Resize popup for screen changes */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    this.overlay.clear();
    this.overlay.rect(0, 0, width, height);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });

    this.popupContainer.x = width / 2;
    this.popupContainer.y = height / 2;

    // Reposition DOM input if in edit mode
    if (this.nicknameInput) {
      this.positionNicknameInput();
    }
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private drawBackground(): void {
    const { width, height, fill, stroke, cornerRadius } = STYLES.popup;

    this.background.clear();
    this.background.roundRect(-width / 2, -height / 2, width, height, cornerRadius);
    this.background.fill(fill);
    this.background.setStrokeStyle({ width: 2, color: stroke });
    this.background.stroke();
  }

  private fetchProfile(): void {
    const state = accountService.getLastState();
    if (state) {
      // Extract nickname from profile
      let nickname: string | null = null;
      if (state.profile) {
        const decoded = decodeProfile(state.profile);
        if (decoded && typeof decoded.nickname === 'string') {
          nickname = decoded.nickname;
        }
      }
      this.showProfile(state.username, state.chips, nickname);
    } else {
      this.showError('Account data not available');
    }
  }

  private showProfile(username: string, chips: number, nickname: string | null): void {
    this.loadingText.visible = false;
    this.nicknameErrorText.visible = false;

    // Display name: nickname if set, otherwise username
    const displayName = nickname ?? username;
    this.displayNameText.text = displayName;
    this.displayNameText.visible = true;

    // Hint text: show @username when nickname is set
    if (nickname) {
      this.nicknameHintText.text = `@${username}`;
    } else {
      this.nicknameHintText.text = 'No nickname set';
    }
    this.nicknameHintText.visible = true;

    this.chipsLabelText.visible = true;

    this.chipsValueText.text = formatBalance(chips);
    this.chipsValueText.visible = true;
  }

  private showError(message: string): void {
    this.loadingText.visible = false;

    this.nicknameErrorText.text = message;
    this.nicknameErrorText.visible = true;
  }

  // ===========================================================================
  // NICKNAME EDIT MODE
  // ===========================================================================

  private enterEditMode(): void {
    if (this.isEditing) return;
    this.isEditing = true;

    // Hide display elements
    this.displayNameText.visible = false;
    this.nicknameHintText.visible = false;
    this.nicknameErrorText.visible = false;
    this.editNicknameButton.visible = false;

    // Create DOM input
    this.nicknameInput = document.createElement('input');
    this.nicknameInput.type = 'text';
    this.nicknameInput.maxLength = NICKNAME_MAX_LENGTH;
    this.nicknameInput.placeholder = 'Enter nickname';

    // Pre-fill with current nickname
    const state = accountService.getLastState();
    if (state?.profile) {
      const decoded = decodeProfile(state.profile);
      if (decoded && typeof decoded.nickname === 'string') {
        this.nicknameInput.value = decoded.nickname;
      }
    }

    // Style the input to match popup theme
    this.nicknameInput.style.cssText = `
      background: #2a2a4e;
      border: 2px solid #d4af37;
      color: #ffffff;
      font-size: 18px;
      font-family: Roboto, Arial, sans-serif;
      text-align: center;
      border-radius: 8px;
      padding: 6px 12px;
      width: 200px;
      outline: none;
      box-sizing: border-box;
      position: absolute;
      z-index: 1000;
    `;

    this.positionNicknameInput();
    document.body.appendChild(this.nicknameInput);
    this.nicknameInput.focus();
    this.nicknameInput.select();

    // Handle Enter key
    this.nicknameInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        this.saveNickname();
      } else if (event.key === 'Escape') {
        this.exitEditMode();
      }
    });

    // Create Save button
    const saveBtnWidth = 80;
    const saveBtnHeight = 30;
    const halfHeight = STYLES.popup.height / 2;
    this.saveNicknameButton = new Button({
      label: 'Save',
      width: saveBtnWidth,
      height: saveBtnHeight,
      style: { ...GOLD_BUTTON_STYLE, fontSize: 13 },
    });
    this.saveNicknameButton.onClick = () => { this.saveNickname(); };
    this.saveNicknameButton.x = -saveBtnWidth - 5;
    this.saveNicknameButton.y = halfHeight - 90;
    this.popupContainer.addChild(this.saveNicknameButton);

    // Create Cancel button
    const cancelBtnWidth = 80;
    this.cancelNicknameButton = new Button({
      label: 'Cancel',
      width: cancelBtnWidth,
      height: saveBtnHeight,
      style: { ...PRIMARY_BUTTON_STYLE, fontSize: 13 },
    });
    this.cancelNicknameButton.onClick = () => { this.exitEditMode(); };
    this.cancelNicknameButton.x = 5;
    this.cancelNicknameButton.y = halfHeight - 90;
    this.popupContainer.addChild(this.cancelNicknameButton);

    // Listen for server responses
    accountService.on('nicknameUpdated', this.onNicknameSuccess);
    accountService.on('accountError', this.onNicknameError);
  }

  private exitEditMode(): void {
    if (!this.isEditing) return;
    this.isEditing = false;

    // Remove DOM input
    if (this.nicknameInput) {
      this.nicknameInput.remove();
      this.nicknameInput = null;
    }

    // Remove Save/Cancel buttons
    if (this.saveNicknameButton) {
      this.saveNicknameButton.destroy();
      this.saveNicknameButton = null;
    }
    if (this.cancelNicknameButton) {
      this.cancelNicknameButton.destroy();
      this.cancelNicknameButton = null;
    }

    // Remove listeners
    accountService.off('nicknameUpdated', this.onNicknameSuccess);
    accountService.off('accountError', this.onNicknameError);

    // Refresh profile display
    this.nicknameErrorText.visible = false;
    this.editNicknameButton.visible = true;
    this.fetchProfile();
  }

  private saveNickname(): void {
    if (!this.nicknameInput) return;

    const trimmedNickname = this.nicknameInput.value.trim();
    if (trimmedNickname.length === 0) {
      this.nicknameErrorText.text = 'Nickname cannot be empty';
      this.nicknameErrorText.visible = true;
      this.nicknameHintText.visible = false;
      return;
    }

    // Disable input while saving
    this.nicknameInput.disabled = true;
    if (this.saveNicknameButton) {
      this.saveNicknameButton.disabled = true;
    }

    // Send command to server
    networkService.send(ServiceGLIDs.ACCOUNT, Commands.setNickname(trimmedNickname));
  }

  private positionNicknameInput(): void {
    if (!this.nicknameInput) return;

    // Position at the display name's y offset (-40) relative to popup center
    const inputLeft = this.screenWidth / 2;
    const inputTop = this.screenHeight / 2 - 35;

    this.nicknameInput.style.left = `${String(inputLeft)}px`;
    this.nicknameInput.style.top = `${String(inputTop)}px`;
    this.nicknameInput.style.transform = 'translate(-50%, -50%)';
  }

  // Arrow methods for proper off() cleanup
  private onNicknameSuccess = (_nickname: string): void => {
    this.exitEditMode();
  };

  private onNicknameError = (errorInfo: AccountErrorInfo): void => {
    // Re-enable input
    if (this.nicknameInput) {
      this.nicknameInput.disabled = false;
    }
    if (this.saveNicknameButton) {
      this.saveNicknameButton.disabled = false;
    }

    // Show error
    this.nicknameErrorText.text = errorInfo.message;
    this.nicknameErrorText.visible = true;
    this.nicknameHintText.visible = false;
  };

  // ===========================================================================
  // AVATAR DISPLAY
  // ===========================================================================

  private drawAvatarDisplay(): void {
    const { size, borderColor, borderWidth, backgroundColor } = STYLES.avatar;
    const radius = size / 2;

    // Gold border circle
    const border = new Graphics();
    border.circle(0, 0, radius);
    border.fill(borderColor);
    this.avatarContainer.addChild(border);

    // Inner background circle
    const bg = new Graphics();
    bg.circle(0, 0, radius - borderWidth);
    bg.fill(backgroundColor);
    this.avatarContainer.addChild(bg);

    // Load avatar sprite if hero has one selected
    const avatarId = avatarService.getAvatarId();
    if (avatarId !== null) {
      const path = getAvatarPath(avatarId);
      void Assets.load<Texture>(path).then((texture) => {
        if (this.destroyed) return;

        const sprite = new Sprite(texture);
        sprite.width = size - borderWidth * 2;
        sprite.height = size - borderWidth * 2;
        sprite.anchor.set(0.5);

        // Circular mask
        const mask = new Graphics();
        mask.circle(0, 0, radius - borderWidth);
        mask.fill(0xffffff);
        this.avatarContainer.addChild(mask);

        sprite.mask = mask;
        this.avatarContainer.addChild(sprite);
      });
    } else {
      // Show placeholder text "?"
      const placeholder = new Text({
        text: '?',
        style: {
          fill: 0xffffff,
          fontSize: 28,
          fontFamily: 'Roboto, Arial, sans-serif',
          fontWeight: 'bold',
        },
      });
      placeholder.anchor.set(0.5);
      this.avatarContainer.addChild(placeholder);
    }
  }

  // ===========================================================================
  // DISMISS & CLEANUP
  // ===========================================================================

  private handleDismiss(): void {
    this.onDismiss?.();
    this.destroy();
  }

  override destroy(): void {
    // Clean up edit mode resources BEFORE super.destroy()
    if (this.nicknameInput) {
      this.nicknameInput.remove();
      this.nicknameInput = null;
    }

    // Remove event listeners
    accountService.off('nicknameUpdated', this.onNicknameSuccess);
    accountService.off('accountError', this.onNicknameError);

    super.destroy({ children: true });
  }
}
